/**
 * @file Renders an email HTML preview in a sandboxed iframe that resizes to fit its content.
 */
import React, { useRef, useState, useEffect, useCallback } from "react";
import type { CSSProperties } from "react";

type PreviewOptions = {
  style?: CSSProperties;
};

type PreviewApp = {
  setContent: (html: string, options: PreviewOptions) => Promise<number>;
};

type PreviewWindow = Window & {
  app?: PreviewApp;
};

type Props = {
  html?: string;
  style?: CSSProperties;
  isMobile?: boolean;
};

const PREVIEW_SRC = "/static/preview.html";
const MOBILE_WIDTH = "420px";
const FULL_WIDTH = "100%";

/**
 * Renders the HTML in an iframe and grows the iframe to the height of the rendered content.
 */
const EmailPreview = ({
  html = "",
  style = undefined,
  isMobile = false,
}: Props): JSX.Element => {
  const iframe = useRef<HTMLIFrameElement>(null);

  const [ height, setHeight ] = useState(0);

  const updateContent = useCallback(async () => {
    const previewWindow = iframe.current?.contentWindow as PreviewWindow | null | undefined;
    const app = previewWindow?.app;

    if (!app) {
      return;
    }

    const options: PreviewOptions = {};

    if (style) {
      options.style = style;
    }

    const updatedHeight = await app.setContent(html, options);

    setHeight(updatedHeight);
  }, [ style, html ]);

  useEffect(() => {
    updateContent();
  }, [ updateContent ]);

  /**
   * Re-renders the content once the preview iframe has loaded.
   */
  const onLoad = () => updateContent();

  const width = isMobile
    ? MOBILE_WIDTH
    : FULL_WIDTH;

  const iframeProps = {
    src: PREVIEW_SRC,
    height: `${height}px`,
    frameBorder: "0",
    width,
    onLoad,
  };

  return (
    <div>
      <iframe
        ref={iframe}
        title="preview"
        {...iframeProps}
      />
    </div>
  );
};

export default EmailPreview;
